import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { useExternalRegisterImport } from "./externalRegisterImportProvider";
import { StageUnavailable, WorkflowCard } from "../shared/ExternalRegisterSettingsUi";
import { IMPORT_TARGET_FIELDS } from "./externalRegisterImportTypes";

export const MappingStage = () => {
  const { t } = useTranslation();
  const {
    parseResponse,
    mapping,
    mappingErrors,
    setMappingField,
    hasHeader,
    validatePreview,
    isValidating,
    reviewReadonly,
  } = useExternalRegisterImport();

  return (
    <WorkflowCard title={t("externalRegister.import.mapping.title")}>
      <Stack spacing={2}>
        {!parseResponse && (
          <StageUnavailable message={t("externalRegister.import.mapping.unavailable")} />
        )}
        <Alert severity="info" variant="outlined">
          {t("externalRegister.import.mapping.info")}
        </Alert>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(220px, 1fr))",
              lg: "repeat(4, minmax(220px, 1fr))",
            },
            gap: 2,
            alignItems: "start",
            maxWidth: 1320,
          }}
        >
          {IMPORT_TARGET_FIELDS.map((definition) => (
            <FormControl key={definition.field} fullWidth size="small">
              <InputLabel id={`${definition.field}-label`}>
                {t(`externalRegister.import.fieldLabels.${definition.field}`)}{definition.required ? " *" : ""}
              </InputLabel>
              <Select
                labelId={`${definition.field}-label`}
                label={`${t(`externalRegister.import.fieldLabels.${definition.field}`)}${definition.required ? " *" : ""}`}
                value={mapping[definition.field] ?? ""}
                onChange={(event) => setMappingField(definition.field, String(event.target.value))}
                disabled={reviewReadonly || !parseResponse}
              >
                <MenuItem value="">{t("externalRegister.import.mapping.placeholder")}</MenuItem>
                {parseResponse?.columns.map((column) => (
                  <MenuItem key={column.index} value={column.index}>
                    {column.index + 1}. {column.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ))}
        </Box>
        {parseResponse && mappingErrors.length > 0 && (
          <Alert severity="info">
            {t("externalRegister.import.mapping.alerts.incomplete", { missing: mappingErrors.join("; ") })}
          </Alert>
        )}
        {parseResponse && mappingErrors.length === 0 && (
          <Alert severity="success">
            {t("externalRegister.import.mapping.alerts.complete")}
          </Alert>
        )}
        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            onClick={validatePreview}
            disabled={reviewReadonly || !parseResponse || hasHeader === null || mappingErrors.length > 0 || isValidating}
            sx={{ width: { xs: "100%", sm: "auto" }, minWidth: 180 }}
          >
            {reviewReadonly
              ? t("externalRegister.import.mapping.buttons.validationCompleted")
              : isValidating
                ? t("externalRegister.import.mapping.buttons.validating")
                : t("externalRegister.import.mapping.buttons.validatePreview")}
          </Button>
        </Stack>
      </Stack>
    </WorkflowCard>
  );
};
